import { Box, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-pro'
import dayjs, { Dayjs } from 'dayjs'
import type { ConfigType } from 'dayjs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  invalidateAppFeatureCache,
  useGetCustomerAppFeatureMappings,
  useUpdateCustomerAppFeatureMappings,
} from 'src/api/app-features'
import { CustomerAppFeatureMappingRow, UpdateCustomerAppFeatureMappingItem } from 'src/generated-types/app-feature'
import { isGlobalOnlyAppFeature } from 'src/constants/appFeatureKeys'
import BasicDatePicker from 'src/components/DateTimePicker'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import GenericTable from 'src/components/table/GenericTable'
import { ShowDialogProps } from 'src/show-dialog'

type RowState = CustomerAppFeatureMappingRow & {
  startDateValue: Dayjs | null
  endDateValue: Dayjs | null
}

type CustomerFeatureFlagsDialogProps = ShowDialogProps & {
  customerId: EntityId
  customerName?: string
}

const toDayjs = (value: ConfigType | null | undefined): Dayjs | null => {
  if (value == null) {
    return null
  }
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.startOf('day') : null
}

const toPickerDate = (value: Dayjs | null): Date | null => (value?.isValid() ? value.toDate() : null)

const toApiDate = (value: Dayjs | null): string | null => (value?.isValid() ? value.format('YYYY-MM-DD') : null)

export const CustomerFeatureFlagsDialog: FC<CustomerFeatureFlagsDialogProps> = ({ pop, customerId, customerName }) => {
  const { t } = useTranslation(['feature-flags', 'common', 'admin-customer'])
  const { data, isLoading, isFetching } = useGetCustomerAppFeatureMappings(Number(customerId))
  const { mutateAsync, isLoading: isSaving } = useUpdateCustomerAppFeatureMappings(Number(customerId))
  const [rows, setRows] = useState<RowState[]>([])

  useEffect(() => {
    if (data) {
      setRows(
        data
          .filter(row => !isGlobalOnlyAppFeature(row.key))
          .map(row => ({
            ...row,
            startDateValue: toDayjs(row.startDate),
            endDateValue: toDayjs(row.endDate),
          })),
      )
    }
  }, [data])

  const onToggle = useCallback((appFeatureToggleId: number, checked: boolean) => {
    setRows(prev =>
      prev.map(row => (row.appFeatureToggleId === appFeatureToggleId ? { ...row, isEnabled: checked } : row)),
    )
  }, [])

  const onStartDateChange = useCallback((appFeatureToggleId: number, value: Date | null) => {
    setRows(prev =>
      prev.map(row =>
        row.appFeatureToggleId === appFeatureToggleId ? { ...row, startDateValue: toDayjs(value) } : row,
      ),
    )
  }, [])

  const onEndDateChange = useCallback((appFeatureToggleId: number, value: Date | null) => {
    setRows(prev =>
      prev.map(row => (row.appFeatureToggleId === appFeatureToggleId ? { ...row, endDateValue: toDayjs(value) } : row)),
    )
  }, [])

  const save = async () => {
    const invalidRange = rows.find(row => {
      if (!row.isEnabled || !row.globalEnabled) {
        return false
      }
      const start = toDayjs(row.startDateValue)
      const end = toDayjs(row.endDateValue)
      return start != null && end != null && start.isAfter(end, 'day')
    })
    if (invalidRange) {
      toast.error(t('customer-date-range-error', { name: invalidRange.displayName }))
      return
    }

    const invalidDate = rows.find(row => {
      if (!row.isEnabled || !row.globalEnabled) {
        return false
      }
      const hasInvalidStart = row.startDateValue != null && !toDayjs(row.startDateValue)?.isValid()
      const hasInvalidEnd = row.endDateValue != null && !toDayjs(row.endDateValue)?.isValid()
      return hasInvalidStart || hasInvalidEnd
    })
    if (invalidDate) {
      toast.error(t('customer-date-invalid', { name: invalidDate.displayName }))
      return
    }

    const payload: UpdateCustomerAppFeatureMappingItem[] = rows.map(row => ({
      appFeatureToggleId: row.appFeatureToggleId,
      isEnabled: row.isEnabled && row.globalEnabled,
      startDate: row.isEnabled ? toApiDate(toDayjs(row.startDateValue)) : null,
      endDate: row.isEnabled ? toApiDate(toDayjs(row.endDateValue)) : null,
    }))

    try {
      await mutateAsync(payload, {
        onSuccess: () => {
          invalidateAppFeatureCache.getCustomerAppFeatureMappings(Number(customerId))
          invalidateAppFeatureCache.getAppFeatureMap([customerId])
        },
      })
      toast.success(t('customer-saved'))
      pop(true)
    } catch {
      toast.error(t('customer-save-failed'))
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'key', headerName: t('col-key'), flex: 1, minWidth: 120 },
      { field: 'displayName', headerName: t('col-name'), flex: 1, minWidth: 160 },
      {
        field: 'globalEnabled',
        headerName: t('col-global'),
        width: 100,
        renderCell: ({ row }) => (
          <Typography variant="body2" color={row.globalEnabled ? 'success.main' : 'text.disabled'}>
            {row.globalEnabled ? t('toggle-enable') : t('toggle-disable')}
          </Typography>
        ),
      },
      {
        field: 'startDate',
        headerName: t('col-start'),
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <BasicDatePicker
            name={`start-${row.appFeatureToggleId}`}
            label=""
            isWithoutAppForm
            value={toPickerDate(row.startDateValue)}
            onChange={value => onStartDateChange(row.appFeatureToggleId, value)}
            disabled={!row.isEnabled || !row.globalEnabled}
          />
        ),
      },
      {
        field: 'endDate',
        headerName: t('col-end'),
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <BasicDatePicker
            name={`end-${row.appFeatureToggleId}`}
            label=""
            isWithoutAppForm
            value={toPickerDate(row.endDateValue)}
            onChange={value => onEndDateChange(row.appFeatureToggleId, value)}
            disabled={!row.isEnabled || !row.globalEnabled}
          />
        ),
      },
      {
        field: 'isEnabled',
        headerName: t('col-enabled'),
        width: 110,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Tooltip
            title={String(
              !row.globalEnabled
                ? t('customer-global-disabled-hint')
                : row.isEnabled
                ? t('toggle-disable')
                : t('toggle-enable'),
            )}
          >
            <span>
              <Switch
                checked={row.isEnabled}
                disabled={!row.globalEnabled || isSaving}
                onChange={e => onToggle(row.appFeatureToggleId, e.target.checked)}
                inputProps={{ 'aria-label': row.key }}
              />
            </span>
          </Tooltip>
        ),
      },
    ],
    [isSaving, onEndDateChange, onStartDateChange, onToggle, t],
  )

  return (
    <GenericDialogLayout
      title={t('customer-dialog-title', { name: customerName ?? customerId })}
      pop={() => pop()}
      removedCloseButton={false}
      sx={{ pb: 0, minWidth: 720 }}
      actions={[
        <GridActionButton
          key="save-customer-features"
          buttonText={String(t('save', { ns: 'common' }))}
          variant="contained"
          width="140px"
          height="42px"
          loading={isSaving}
          disabled={isSaving || isLoading}
          onClick={() => void save()}
        />,
      ]}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('customer-help')}
        </Typography>
        <Box>
          <GenericTable
            id={`customer-app-features-${customerId}`}
            enableToolbar={false}
            data={rows}
            columns={columns}
            loading={isLoading || isFetching}
            getRowId={row => row.appFeatureToggleId}
            getRowHeight={() => 56}
            autoHeight
          />
        </Box>
      </Stack>
    </GenericDialogLayout>
  )
}
